import type { Ref, ShallowRef } from 'vue'
import type { ResourceItem } from '@/utils/resource'
import type { ResourceMarkType } from '@/constants/resource-marks'
import { getMultipleResources } from '@/utils/resource'

/**
 * 与传入的 marks 元组一一对应的资源数据元组
 */
export type ResourceTuple<T extends readonly ResourceMarkType[]> = {
  -readonly [K in keyof T]: ResourceItem[]
}

export interface UseResourcesOptions {
  /** 是否在调用时立即请求，默认 true */
  immediate?: boolean
}

export interface UseResourcesReturn<T extends readonly ResourceMarkType[]> {
  /** 与 marks 顺序一一对应的资源数据；未返回或请求失败时为空数组 */
  data: Ref<ResourceTuple<T>>
  /** 是否正在请求 */
  loading: Ref<boolean>
  /** 最近一次请求的错误；getMultipleResources 内部已兜底，通常为 null */
  error: ShallowRef<Error | null>
  /** 手动触发请求（首屏、下拉刷新、重试等场景） */
  load: () => Promise<ResourceTuple<T>>
  /** 清空数据并作废仍在途的请求 */
  reset: () => void
}

/**
 * 声明式获取一组资源组数据（基于 getResourceByMark 组合，不改变原 API 行为）。
 *
 * @param marks 资源标识元组，返回值 data 与其下标一一对应；类型由元组推断
 * @param options 配置项，immediate 为 false 时需手动调用 load()
 *
 * @example
 * const { data, loading, load } = useResources(
 *   [INDEX_RESOURCE_MARKS.TOP_BANNER, INDEX_RESOURCE_MARKS.DIAMOND],
 *   { immediate: false },
 * )
 * await load()
 * bannerList.value = data.value[0]
 */
export function useResources<T extends readonly ResourceMarkType[]>(
  marks: T,
  options: UseResourcesOptions = {},
): UseResourcesReturn<T> {
  const { immediate = true } = options

  const createEmptyData = () => marks.map(() => [] as ResourceItem[]) as unknown as ResourceTuple<T>

  const data = ref(createEmptyData()) as Ref<ResourceTuple<T>>
  const loading = ref(false)
  const error = shallowRef<Error | null>(null)

  // 请求序号：只有最后一次请求的结果可以写入状态，避免竞态导致的错乱
  let requestSeq = 0

  const load = async (): Promise<ResourceTuple<T>> => {
    const seq = ++requestSeq
    loading.value = true
    error.value = null

    try {
      const result = await getMultipleResources(Array.from(marks))
      if (seq === requestSeq) {
        data.value =
          result.length === marks.length
            ? (result.map((items) => items || []) as unknown as ResourceTuple<T>)
            : createEmptyData()
      }
    } catch (err) {
      if (seq === requestSeq) {
        error.value = err instanceof Error ? err : new Error(String(err))
        data.value = createEmptyData()
      }
    } finally {
      if (seq === requestSeq) {
        loading.value = false
      }
    }

    return data.value
  }

  const reset = () => {
    requestSeq += 1
    data.value = createEmptyData()
    error.value = null
    loading.value = false
  }

  if (immediate) {
    load()
  }

  return { data, loading, error, load, reset }
}
